import {
  CSSProperties,
  KeyboardEventHandler,
  MouseEventHandler,
  Suspense,
  ComponentType,
} from 'react';

import {isNotNil} from 'ramda-adjunct';

import {getSafely, suffixTestId, TestIdProps} from 'shared/web';

import {TEXT_COLOR_CLASSES} from '../../class-maps/textColorClasses';
import {useResponsiveValue} from '../../hooks/useResponsiveValue';
import {Integer} from '../../types/Integer';
import {ResponsiveValue} from '../../types/ResponsiveValue';
import {ThemeColorPath} from '../../types/ThemePaths';
import {cn} from '../../utils/cn';
import {themeIcons, ThemeIconKey} from '../../utils/icons/themeIcons';
import {IconErrorBoundary} from './IconErrorBoundary';

/**
 * Generic icon registry type
 * Can contain any lazy-loaded or direct icon components
 */
// eslint-disable-next-line -eslint/no-explicit-any
export type IconRegistry = Record<string, ComponentType<any>>;

export interface IconProps<TIconKey extends string = ThemeIconKey> extends TestIdProps {
  /**
   * Icon key from the registry
   * When using custom iconRegistry, this can be any string from that registry
   */
  value: TIconKey | undefined;

  /**
   * Optional custom icon registry
   * If not provided, uses foundation themeIcons
   *
   * @example
   * ```tsx
   * import {miraIcons} from '@mira/ui';
   *
   * <Icon value="mira/chart-bar" iconRegistry={miraIcons} />
   * ```
   */
  iconRegistry?: IconRegistry;

  color?: ThemeColorPath;
  cursor?: CSSProperties['cursor'];
  size?: Integer | ResponsiveValue<Integer>;
  isNotFilled?: boolean;
  tabIndex?: number;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
}

export function Icon<TIconKey extends string = ThemeIconKey>(props: IconProps<TIconKey>) {
  const resolvedSize = useResponsiveValue(props.size ?? 6);
  const sizeInRem = `${resolvedSize * 0.25}rem`;

  if (!props.value) {
    return null;
  }

  // Use custom registry if provided, otherwise use foundation themeIcons
  const iconRegistry = props.iconRegistry ?? themeIcons;
  const IconComponent = getSafely<IconRegistry, TIconKey>(iconRegistry, props.value);
  const FallbackIconComponent = iconRegistry.fallback ?? themeIcons.fallback;

  // Literal class map (not `text-${...}` string building) so Tailwind's static
  // scanner emits the classes into the CSS bundle.
  const colorClass = props.color ? TEXT_COLOR_CLASSES[props.color] : undefined;

  const cursorClass = props.cursor
    ? `cursor-${props.cursor}`
    : (props.onClick && 'cursor-pointer') || undefined;

  const classes = cn(
    'inline-block leading-[0] transition-shadow rounded-sm',
    'w-[var(--icon-size)] h-[var(--icon-size)]',
    'focus-visible:outline-none focus-visible:ring-2',
    colorClass,
    cursorClass
  );

  const style: CSSProperties = {
    '--icon-size': sizeInRem,
  } as CSSProperties;

  return (
    <div
      role="img"
      className={classes}
      style={style}
      onClick={props.onClick}
      onKeyDown={props.onKeyDown}
      tabIndex={props.tabIndex}
      data-iconkey={props.value}
      data-not-filled={props.isNotFilled ? 'true' : undefined}
      data-testid={suffixTestId('icon', props)}
    >
      <IconErrorBoundary icon={props.value} fallback={<FallbackIconComponent />}>
        <Suspense fallback={<FallbackIconComponent />}>
          {isNotNil(IconComponent) && <IconComponent data-iconkey={props.value} />}
        </Suspense>
      </IconErrorBoundary>
    </div>
  );
}
